import { Box, Card, CardBody, Flex, Heading, Hide, List, ListIcon, ListItem, Stack, Text } from '@chakra-ui/react';
import { getCustomerConfig } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { ReactNode } from 'react';
import { HiCheck } from 'react-icons/hi2';

import { useCustomerAsset } from '../../hooks/tenant/useCustomerAsset';
import { useCustomerCopy } from '../../hooks/tenant/useCustomerCopy';
import { CargoPlane } from '../CargoPlane';
import { Logo } from '../Logo';

type Props = {
  heading: string;
  subHeading?: string;
  children: ReactNode;
};

export const AuthLayout = ({ heading, subHeading, children }: Props) => {
  const copy = useCustomerCopy();
  const panelPoster = useCustomerAsset('authPanelPoster');
  const panelVideo = useCustomerAsset('authPanelVideo');

  const hasSubHeading = !!subHeading;
  // Opt-in per tenant: the demo assets are symlinks to Pelicargo's video for everyone.
  // Also off when the tenant has removed the video slot itself - the opt-in flag and the asset
  // are separate switches and either one absent means no player.
  const showPanelVideo = !!getCustomerConfig().authPanel?.showVideo && !!panelVideo;

  return (
    <Flex minH={{ base: 'auto', md: '100vh' }} position="relative" h="full" w="full">
      <Flex w="full" maxW="full" bg="gray.700">
        <Hide below="lg">
          <Flex flex="1" direction="column" align="center" justify="center" h="full" w="full">
            <Stack spacing="12" p="16">
              <Stack spacing="3">
                <Heading variant="h2" color="whiteAlpha.900">
                  {copy('auth.panel.heading')}
                </Heading>
                <Text textStyle="body" color="whiteAlpha.900">
                  {copy('auth.panel.subheading')}
                </Text>
              </Stack>
              <Show if={showPanelVideo}>
                <Box w="450px">
                  {/* No `type`: the slot may be overridden with a format other than mp4, and
                      browsers sniff the container anyway. */}
                  <video controls preload="auto" poster={panelPoster}>
                    <source src={panelVideo} />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Show>

              <List spacing="4" w="full">
                <ListItem textStyle="bodySmall" color="whiteAlpha.900">
                  <ListIcon as={HiCheck} color="green.200" />
                  {copy('auth.panel.bullet1')}
                </ListItem>
                <ListItem textStyle="bodySmall" color="whiteAlpha.900">
                  <ListIcon as={HiCheck} color="green.200" />
                  {copy('auth.panel.bullet2')}
                </ListItem>
                <ListItem textStyle="bodySmall" color="whiteAlpha.900">
                  <ListIcon as={HiCheck} color="green.200" />
                  {copy('auth.panel.bullet3')}
                </ListItem>
              </List>
            </Stack>
          </Flex>
        </Hide>
        <Stack
          justify="center"
          align="center"
          bg="bg.default"
          spacing="16"
          h={{ base: '100vh', lg: 'full' }}
          position="relative"
          flex="2"
        >
          <Stack p="4" py="8" align="center" spacing="8" w="full" zIndex="999">
            <Logo fullSized />
            <Card h="auto" w="full" maxW="600px" bgColor="bg.surface">
              <CardBody p="6">
                <Stack spacing="6">
                  <Stack spacing="1">
                    <Heading as="h1" variant="h3">
                      {heading}
                    </Heading>
                    <Show if={hasSubHeading}>
                      <Text textStyle="bodySmall">{subHeading}</Text>
                    </Show>
                  </Stack>
                  {children}
                </Stack>
              </CardBody>
            </Card>
          </Stack>
          <CargoPlane />
        </Stack>
      </Flex>
    </Flex>
  );
};
